// src/features/TechnicianAnalysis/PartsInventory.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import {
  Inventory,
  Warning,
  Add,
  Remove,
  TrendingUp,
  LocalShipping
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { PartsInventoryService } from '../../services/partsInventoryService';

const PartsInventory = ({ user }) => {
  const { t } = useI18n();
  const [inventoryService] = useState(new PartsInventoryService());
  const [parts, setParts] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [reorderRecommendations, setReorderRecommendations] = useState([]);
  const [usePartDialog, setUsePartDialog] = useState({ open: false, part: null });
  const [restockDialog, setRestockDialog] = useState({ open: false, part: null });
  const [useQuantity, setUseQuantity] = useState(1);
  const [restockQuantity, setRestockQuantity] = useState(1);

  useEffect(() => {
    loadInventoryData();
  }, []);

  const loadInventoryData = () => {
    setParts(inventoryService.parts);
    setPredictions(inventoryService.predictPartsNeeds());
    setReorderRecommendations(inventoryService.getReorderRecommendations());
  };

  const handleUsePart = async () => {
    try {
      await inventoryService.usePart(
        usePartDialog.part.id, 
        useQuantity,
        'machine_001', // Буде з реального контексту
        'Maintenance repair'
      );
      loadInventoryData();
      setUsePartDialog({ open: false, part: null });
      setUseQuantity(1);
    } catch (error) {
      console.error('Error using part:', error);
      alert(t('use_part_error'));
    }
  };

  const handleRestockPart = async () => {
    try {
      await inventoryService.restockPart(restockDialog.part.id, restockQuantity);
      loadInventoryData();
      setRestockDialog({ open: false, part: null });
      setRestockQuantity(1);
    } catch (error) {
      console.error('Error restocking part:', error);
      alert(t('restock_error'));
    }
  };

  const getStockStatus = (part) => {
    if (part.currentStock === 0) return { status: 'out_of_stock', color: 'error' };
    if (part.currentStock <= part.minStock) return { status: 'low_stock', color: 'warning' };
    if (part.currentStock <= part.minStock * 1.5) return { status: 'medium_stock', color: 'info' };
    return { status: 'in_stock', color: 'success' };
  };

  const getStockStatusText = (status) => {
    const statusMap = {
      'out_of_stock': t('out_of_stock'),
      'low_stock': t('low_stock'),
      'medium_stock': t('medium_stock'),
      'in_stock': t('in_stock')
    };
    return statusMap[status] || status;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('parts_inventory_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('parts_inventory_subtitle')}
      </Typography>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Inventory sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              {parts.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('total_parts')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Warning sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" color="error.main" fontWeight="bold">
              {parts.filter(p => p.currentStock <= p.minStock).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('low_stock_parts')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" color="warning.main" fontWeight="bold">
              {reorderRecommendations.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('reorder_recommendations')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <LocalShipping sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" color="info.main" fontWeight="bold">
              €{inventoryService.getInventoryValue().toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('inventory_value')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Рекомендації по замовленню */}
      {reorderRecommendations.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('reorder_recommendations_alert')} ({reorderRecommendations.length})
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {reorderRecommendations.map(rec => (
              <Chip
                key={rec.partId}
                label={`${rec.partName} (${rec.daysOfStock} ${t('days_left')})`}
                size="small"
                color="warning"
              />
            ))}
          </Stack>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Список запчастин */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('parts_list')}
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('part_name')}</strong></TableCell>
                      <TableCell><strong>{t('category')}</strong></TableCell>
                      <TableCell><strong>{t('stock')}</strong></TableCell>
                      <TableCell><strong>{t('status')}</strong></TableCell>
                      <TableCell><strong>{t('cost')}</strong></TableCell>
                      <TableCell><strong>{t('actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parts.map(part => {
                      const stockStatus = getStockStatus(part);
                      return (
                        <TableRow key={part.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {part.name}
                            </Typography>
                            {part.critical && (
                              <Chip label={t('critical')} color="error" size="small" sx={{ mt: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>{t(part.category)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {part.currentStock} / {part.maxStock}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              min: {part.minStock}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStockStatusText(stockStatus.status)}
                              color={stockStatus.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>€{part.cost}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setUsePartDialog({ open: true, part })}
                                disabled={part.currentStock === 0}
                              >
                                <Remove />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setRestockDialog({ open: true, part })}
                              >
                                <Add />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Прогнози та аналітика */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('stock_predictions')}
              </Typography>
              <Stack spacing={2}>
                {predictions.slice(0, 5).map(pred => (
                  <Box key={pred.partId}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                        {pred.partName}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {pred.daysOfStock} {t('days')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4, 
                          bgcolor: pred.daysOfStock < 7 ? 'error.main' : 
                                  pred.daysOfStock < 14 ? 'warning.main' : 'success.main',
                          width: `${Math.min(pred.daysOfStock / 30 * 100, 100)}%`
                        }} 
                      />
                      <Typography variant="caption">
                        {pred.predictedUsage}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Найбільш використовувані */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('most_used_parts')}
              </Typography>
              <Stack spacing={1}>
                {inventoryService.getMostUsedParts(5).map((part, index) => (
                  <Box key={part.partId} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {index + 1}. {part.partName}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {part.usageCount}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Діалог використання запчастини */}
      <Dialog open={usePartDialog.open} onClose={() => setUsePartDialog({ open: false, part: null })}>
        <DialogTitle>{t('use_part')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('using_part')}: <strong>{usePartDialog.part?.name}</strong>
          </Typography>
          <TextField
            label={t('quantity')}
            type="number"
            value={useQuantity}
            onChange={(e) => setUseQuantity(parseInt(e.target.value) || 1)}
            fullWidth
            sx={{ mt: 2 }}
            inputProps={{ min: 1, max: usePartDialog.part?.currentStock }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {t('available_stock')}: {usePartDialog.part?.currentStock}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsePartDialog({ open: false, part: null })}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleUsePart} 
            variant="contained"
            disabled={useQuantity > usePartDialog.part?.currentStock}
          >
            {t('use_part')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог поповнення запасу */}
      <Dialog open={restockDialog.open} onClose={() => setRestockDialog({ open: false, part: null })}>
        <DialogTitle>{t('restock_part')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('restocking_part')}: <strong>{restockDialog.part?.name}</strong>
          </Typography>
          <TextField
            label={t('quantity')}
            type="number"
            value={restockQuantity}
            onChange={(e) => setRestockQuantity(parseInt(e.target.value) || 1)}
            fullWidth
            sx={{ mt: 2 }}
            inputProps={{ min: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {t('current_stock')}: {restockDialog.part?.currentStock}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestockDialog({ open: false, part: null })}>
            {t('cancel')}
          </Button>
          <Button onClick={handleRestockPart} variant="contained">
            {t('restock')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartsInventory;